// tiktok-gallery.service.ts
import { Injectable, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, map, switchMap, catchError, of, Observable, throwError } from 'rxjs';
import { AuthHeaderService } from './auth-header.service';
import { SanctumService } from './sanctum.service';
import { environment } from 'src/environments/environment';
import { HttpParams } from '@angular/common/http';
import { RevalidatedHttpService, RevalidatedResource } from 'src/app/core/http-cache/revalidated-http.service';


@Injectable({ providedIn: 'root' })
export class TiktokGalleryService {
  constructor(
    private http: HttpClient,
    private authHeader: AuthHeaderService,
    private sanctum: SanctumService,
    private revalidatedHttp: RevalidatedHttpService
  ) {
  }

  private baseUrl() {
    return `${environment.apiBaseUrl}/api`;
  }
  private storeId(): number {
    return Number(localStorage.getItem('ss_SelectedStoreId') || 0);
  }

  getAccounts() {
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/tiktok/accounts`;
    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, opts)),
      map(res => Array.isArray(res) ? res : (res.accounts ?? [])),
      catchError(() => of([]))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache).
  getAccountsRevalidated(): RevalidatedResource<any[]> {
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/tiktok/accounts`;
    const cacheKey = `tiktok_gallery_accounts:store:${this.storeId()}`;

    const resource = this.revalidatedHttp.getSignal<any>(
      cacheKey,
      url,
      this.authHeader.getHttpOptions()
    );

    const data = computed(() => {
      const res = resource.data();
      if (res === null) return null;
      return Array.isArray(res) ? res : (res.accounts ?? []);
    });

    return { ...resource, data };
  }

  listUserVideos(openId: string, count = 25, cursor?: number | null) {
    const params = new URLSearchParams();
    params.set('open_id', openId);
    params.set('count', String(count));
    if (cursor != null) params.set('cursor', String(cursor));

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/tiktok/videos?${params.toString()}`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, opts))
    );
  }

  listTikTokVideos(openId: string, limit: number = 10, cursor?: string): Observable<any> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/tiktok/videos`;

    let params = new HttpParams()
      .set('open_id', openId)
      .set('limit', limit.toString());
  
    if (cursor) params = params.set('cursor', cursor);
  
    return from(this.authHeader.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<any>(url, { ...httpOptions, params })
      ),
      catchError(err => throwError(() => err))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache).
  listTikTokVideosRevalidated(openId: string, limit: number = 10, cursor?: string): RevalidatedResource<any> {
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/tiktok/videos`;

    let params = new HttpParams()
      .set('open_id', openId)
      .set('limit', limit.toString());

    if (cursor) params = params.set('cursor', cursor);

    const cacheKey = `tiktok_videos:store:${this.storeId()}:${openId}:${limit}:${cursor ?? ''}`;

    return this.revalidatedHttp.getSignal<any>(
      cacheKey,
      url,
      this.authHeader.getHttpOptions(),
      { params }
    );
  }


  extractTikTokByUrlPublic(urlToExtract: string) {
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/tiktok/extract`;
    const params: any = { url: urlToExtract };
  
    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: null
      }))
      // Não use catchError aqui! Deixe o erro subir para o subscribe do componente
    );
  }
}
